import React, { useMemo } from 'react';
import Select from 'react-select';
import { Country, State, City } from 'country-state-city';
import { countryflagCode } from './countryflagCode';

// Custom styles to match existing input design
const customStyles = {
  control: (provided) => ({
    ...provided,
    height: '42px',
    minHeight: '42px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#9ca3af',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '42px',
    padding: '0 8px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '42px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#e5e7eb' : state.isFocused ? '#f3f4f6' : 'white',
    color: '#111827',
    '&:active': {
      backgroundColor: '#d1d5db',
    },
  }),
};

export default function LocationSelector({
  countryCode,
  state,
  setState,
  district,
  setDistrict,
}) {
  const isIndia = countryCode === "+91";
  // Find country from countryflagCode using countryCode
  const selectedCountry = useMemo(() => {
    return countryflagCode.find(
      (country) => country.countryCode === countryCode
    );
  }, [countryCode]);

  // Find country ISO code from country-state-city library
  const countryIsoCode = useMemo(() => {
    if (!selectedCountry) return null;
    const countries = Country.getAllCountries();
    const matchedCountry = countries.find(
      (c) => c.isoCode.toLowerCase() === selectedCountry.flagCode.toLowerCase()
    );
    return matchedCountry?.isoCode || null;
  }, [selectedCountry]);

  // Get states for the country
  const states = useMemo(() => {
    if (!countryIsoCode) return [];
    return State.getStatesOfCountry(countryIsoCode);
  }, [countryIsoCode]);

  // Get cities for the selected state
  const cities = useMemo(() => {
    if (!countryIsoCode || !state) return [];
    const stateObj = states.find((s) => s.name === state);
    if (!stateObj) return [];
    return City.getCitiesOfState(countryIsoCode, stateObj.isoCode);
  }, [countryIsoCode, state, states]);



  const stateOptions = states.map((s) => ({
    value: s.name,
    label: s.name,
  }));

  const cityOptions = cities.map((c) => ({
    value: c.name,
    label: c.name,
  }));

  const handleStateChange = (option) => {
    setState(option?.value || '');
    setDistrict('');
  };

  const handleDistrictChange = (option) => {
    setDistrict(option?.value || '');
  };

  const selectedStateOption = state ? stateOptions.find((opt) => opt.value === state) : null;
  const selectedDistrictOption = district ? cityOptions.find((opt) => opt.value === district) : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
      <div>
        <label className="font-semibold block mb-1">
          State<span className="text-red-500">*</span>
        </label>
        <Select
          styles={customStyles}
          options={stateOptions}
          value={selectedStateOption}
          onChange={handleStateChange}
          placeholder="Select State"
          isSearchable
          isDisabled={!countryIsoCode}
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
        />
      </div>
      <div>
        <label className="font-semibold block mb-1">
          City / District{isIndia && <span className="text-red-500">*</span>}
        </label>
        <Select
          styles={customStyles}
          options={cityOptions}
          value={selectedDistrictOption}
          onChange={handleDistrictChange}
          placeholder="Select District"
          isSearchable
          isDisabled={!state}
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
        />
      </div>
    </div>
  );
}